"use client";

import Link from "next/link";
import type { ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { REQUEST_ACCESS_PATH } from "@/lib/contact";
import { OPEN_BOOKING_MODAL_EVENT } from "@/lib/constants";
import { cn } from "@/lib/utils";

export function openRequestDemo(source?: string) {
  window.dispatchEvent(
    new CustomEvent(
      OPEN_BOOKING_MODAL_EVENT,
      source ? { detail: { source } } : undefined,
    ),
  );
}

interface RequestAccessLinkProps {
  children: ReactNode;
  /** Analytics source forwarded to the booking modal event. */
  source?: string;
  className?: string;
  "aria-label"?: string;
}

/**
 * A Request Access control that is a real link first and a modal second.
 *
 * The href is the canonical /request-access page, so the control works with
 * JavaScript disabled, can be opened in a new tab, and gives crawlers a URL to
 * follow. When script is available the click is intercepted and the existing
 * booking modal opens instead, which keeps the faster in-page flow. Modifier
 * and non-primary clicks fall through to normal link behavior.
 */
export function RequestAccessLink({
  children,
  source,
  className,
  ...props
}: RequestAccessLinkProps) {
  return (
    <Link
      href={REQUEST_ACCESS_PATH}
      className={className}
      onClick={(event) => {
        if (
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey ||
          event.button !== 0
        ) {
          return;
        }
        event.preventDefault();
        openRequestDemo(source);
      }}
      {...props}
    >
      {children}
    </Link>
  );
}

interface MarketingPrimaryCTAProps {
  children: ReactNode;
  /** Analytics source forwarded to the booking modal event. */
  source?: string;
  /** Render as a link instead of opening the booking modal. */
  href?: string;
  className?: string;
  "aria-label"?: string;
}

export function MarketingPrimaryCTA({
  children,
  source,
  href,
  className,
  ...props
}: MarketingPrimaryCTAProps) {
  const ctaClassName = cn("group h-11 px-5 max-[480px]:w-full", className);

  if (href) {
    return (
      <Button asChild size="lg" className={ctaClassName} {...props}>
        <Link href={href}>{children}</Link>
      </Button>
    );
  }

  return (
    <Button
      size="lg"
      onClick={() => openRequestDemo(source)}
      className={ctaClassName}
      {...props}
    >
      {children}
    </Button>
  );
}

interface MarketingSecondaryCTAProps {
  children: ReactNode;
  href: string;
  className?: string;
  "aria-label"?: string;
}

export function MarketingSecondaryCTA({
  children,
  href,
  className,
  ...props
}: MarketingSecondaryCTAProps) {
  return (
    <Button
      asChild
      variant="outline"
      size="lg"
      className={cn("group h-11 px-5 max-[480px]:w-full", className)}
      {...props}
    >
      <Link href={href}>{children}</Link>
    </Button>
  );
}
